import { Input } from 'antd';
import { useState } from 'react';

import { Modal } from '@/components/feedback/modal';
import { useToast } from '@/components/feedback/toast';

import { useRenameDevice } from '../hooks';
import type { Device } from '../types';

import styles from './rename-device-modal.module.scss';

export type RenameDeviceModalProps = {
  open: boolean;
  device: Device;
  onClose: () => void;
};

export function RenameDeviceModal({ open, device, onClose }: RenameDeviceModalProps) {
  const toast = useToast();
  const rename = useRenameDevice(device.id);
  const [name, setName] = useState(device.name);

  // Re-seed the field to the device name when the modal (re)opens.
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setName(device.name);
  }

  const trimmed = name.trim();
  const valid = trimmed.length > 0 && trimmed.length <= 60;

  const save = () => {
    if (!valid) return;
    rename.mutate(trimmed, {
      onSuccess: () => {
        toast.success({ title: 'Device renamed' });
        onClose();
      },
      onError: () => toast.error({ title: 'Could not rename device' }),
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Rename Device"
      size="sm"
      loading={rename.isPending}
      primaryText="Save"
      primaryVariant="neutral"
      onPrimary={save}
      cancelText="Cancel"
    >
      <label className={styles.field}>
        <span className={styles.label}>Device name</span>
        <Input
          value={name}
          maxLength={60}
          autoFocus
          onChange={(e) => setName(e.target.value)}
          onPressEnter={save}
          status={!valid && trimmed.length > 0 ? 'error' : undefined}
          placeholder="Enter a device name"
        />
        {trimmed.length === 0 ? <span className={styles.hint}>Name is required.</span> : null}
      </label>
    </Modal>
  );
}
